import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { terminal } from "../../utils/user-helper";

interface CategoryType {
  id: string;
  name: string;
}

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "Food" | "Drink" | "Laundry" | "Game" | "Room";
  categories: CategoryType[];
  onSave: (data: any) => void;
  isEdit?: boolean;
  autofillData?: any;
}

const AddItemModal = ({
  isOpen,
  onClose,
  type,
  categories,
  onSave,
  isEdit = false,
  autofillData = {
    name: "",
    category: "",
    newCategory: "",
    price: "",
    stock: type === "Food" || type === "Drink" ? "" : undefined,
    status: type === "Game" || type === "Room" || type === "Laundry" ? "Available" : undefined,
  },
}: AddItemModalProps) => {

  const tm = terminal();

  const [formData, setFormData] = useState<any>(autofillData);
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  // ✅ Re-sync when editing an item
  useEffect(() => {
    if (isOpen) {
      // console.log(autofillData);
      setFormData(autofillData);
      setIsAddingCategory(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    let category;

    if (formData.newCategory?.trim()) {
      // ✅ User entered a new category
      category = {
        id: uuidv4(),
        name: formData?.newCategory?.toLowerCase()?.trim(),
        isNew: true,
      };
    } else if (formData.category) {
      // ✅ User selected existing category
      const selected = JSON.parse(formData.category);
      category = {
        id: selected.id,
        name: selected?.name ?? "",
        isNew: false,
      };
    }

    const payload = {
      item: {
        ...formData,
        categoryId: category?.id,
      },
      category,
    };

    // console.log("Saved payload:", payload);
    onSave(payload);

    // ✅ Reset fields after save
    setFormData({
      name: "",
      category: "",
      newCategory: "",
      price: "",
      stock: type === "Food" || type === "Drink" ? "" : undefined,
      status: type === "Game" || type === "Room" ? "Available" : undefined,
    });
    setIsAddingCategory(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
   <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-lg space-y-4
                max-h-[90vh] overflow-y-auto">

        {!isEdit && <h2 className="text-xl text-center font-semibold">Add New {type}</h2>}
        {isEdit && <h2 className="text-xl text-center font-semibold">Edit {type}</h2>}

        {/* Name */}
        <input
          type="text"
          name="name"
          placeholder={`${type} Name`}
          value={formData.name}
          title={`${type} Name`}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-3 py-2 capitalize"
        />

        {/* Category */}
        {(type !== "Room" && !isAddingCategory) ? (
          <div className="flex gap-2">
            {!isEdit && <select
              name="category"
              value={formData.category}
              title={`${type} Category`}
              onChange={handleChange}
              className="flex-1 border border-gray-300 rounded px-3 py-2 capitalize"
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.name} value={JSON.stringify({ id: c.id, name: c.name })}>
                  {c.name}
                </option>
              ))}
            </select>}
            {!isEdit && <button
              type="button"
              onClick={() => setIsAddingCategory(true)}
              className="px-3 py-2 bg-green-500 text-white rounded"
            >
              
              + New
            </button>}
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              name="newCategory"
              placeholder={type === "Room" ? "Room No." : `${type} category`}
              value={formData.newCategory}
              title={`${type} Category`}
              onChange={handleChange}
              className="flex-1 border border-gray-300 rounded px-3 py-2 capitalize"
            />
            {(type !== "Room" && !isEdit) && <button
              type="button"
              onClick={() => setIsAddingCategory(false)}
              className="px-3 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>}
          </div>
        )}

        {/* Price */}<input
          type="number"
          name="price"
          title={`${type} Price`}
          placeholder={"Standard Price"}
          value={formData.price}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-3 py-2"
        />

        {tm === "t2" && (type === "Food" || type === "Drink") && <>
          {/* <input
            type="number"
            name="cashPrice"
            title={"Room Rate"}
            placeholder="Room Rate"
            value={formData.price}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          /> */}
          
          <input
            type="number"
            name="openBar"
            title="Open Bar Price"
            placeholder="Open Bar Price"
            value={formData.openBar}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />

          <input
            type="number"
            name="gameHouse"
            title="Game House Price"
            placeholder="Game House Price"
            value={formData.gameHouse}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />

          <input
            type="number"
            name="vipLounge"
            title="Vip Lounge Price"
            placeholder="Vip Lounge Price"
            value={formData.vipLounge}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />

          <input
            type="number"
            name="privateLounge"
            title="Private Lounge Rate"
            placeholder="Private Lounge Rate"
            value={formData.privateLounge}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />

          <input
            type="number"
            name="clubHouse"
            title="Club House Price"
            placeholder="Club House Price"
            value={formData.clubHouse}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        </>}

        {/* Stock (Food/Drink only) */}
        {(type === "Food" || type === "Drink") && (
          <input
            title={`${type} Stock`}
            type="number"
            name="stock"
            placeholder="Stock Quantity"
            value={formData.stock}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        )}

        {/* Status (Game/Room only) */}
        {(type === "Game" || type === "Laundry") && (
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          >
            <option value="Available">Available</option>
            <option value="Unavailable">Unavailable</option>
            {/* {type === "Room" && <option value="Maintenance">Maintenance</option>} */}
          </select>
        )}

        {/* Description */}
        {/* <input
          type="text"
          name="description"
          title={"Description"}
          placeholder="Description"
          value={formData.description}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-3 py-2"
        /> */}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;